import { SimpleGrid } from '@mantine/core'
import { IconUsers, IconBuilding, IconChalkboard, IconCalendar, IconBrandGoogle } from '@tabler/icons-react'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import { useEffect, useState } from 'react'
import { useArcTranslation } from 'hooks'
import { useClassroomIntegrationEnabled } from 'integrations/classroom'
// Reused on purpose: School Settings cards must be visual twins of the
// Reports hub cards (same tile, typography, paddings and hover).
import { ReportCard } from 'pages/ArcReports/components/ReportCard'

// Same grid contract as the Reports hub (ArcReportsContent)
const GRID_BREAKPOINTS = [
  { maxWidth: 'md', cols: 2 },
  { maxWidth: 'sm', cols: 1 },
]

function useCanManageSchool() {
  const [canManage, setCanManage] = useState(false)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function check() {
      try {
        const response = await api.get(ARC_MOSAICO_ENDPOINTS.tutors.list, {
          params: { page: 1, limit: 1 },
        })
        if (!cancelled) setCanManage(response.status === 200)
      } catch {
        if (!cancelled) setCanManage(false)
      } finally {
        if (!cancelled) setChecked(true)
      }
    }
    check()
    return () => { cancelled = true }
  }, [])

  return { canManage, checked }
}

export function SettingsGrid() {
  const { t } = useArcTranslation()
  const { canManage, checked } = useCanManageSchool()
  const { enabled: classroomEnabled } = useClassroomIntegrationEnabled()

  if (!checked) return null

  return (
    <SimpleGrid cols={3} spacing='md' breakpoints={GRID_BREAKPOINTS}>
      {canManage && (
        <ReportCard
          icon={<IconBuilding size={24} />}
          color='indigo'
          title={t('school_settings.card_school_details_title')}
          description={t('school_settings.card_school_details_description')}
          to='/teacher/school-details'
        />
      )}
      <ReportCard
        icon={<IconUsers size={24} />}
        color='cyan'
        title={t('school_settings.card_manage_students_title')}
        description={t('school_settings.card_manage_students_description')}
        to='/teacher/manage-students'
      />
      {canManage && (
        <ReportCard
          icon={<IconChalkboard size={24} />}
          color='orange'
          title={t('school_settings.card_manage_faculty_title')}
          description={t('school_settings.card_manage_faculty_description')}
          to='/teacher/manage-faculty'
        />
      )}
      {/* Way into the legacy school year screens, which are the only place where
          school years and terms can be seen and edited. They live on Rails,
          outside the SPA, so this uses ReportCard's `external` mode for a full
          page load. Gated like the other admin cards: the screen is manager-only. */}
      {canManage && (
        <ReportCard
          icon={<IconCalendar size={24} />}
          color='grape'
          title={t('school_settings.card_school_years_title')}
          description={t('school_settings.card_school_years_description')}
          to='/manager/school_years'
          external
        />
      )}
      {/* Classroom is off until the school is granted arc-google-classroom,
          so the way in has to disappear with it — not just the page. Unlike
          the admin cards, every tutor gets in: the Workspace connection is
          per user (each teacher connects their own Google account). */}
      {classroomEnabled && (
        <ReportCard
          icon={<IconBrandGoogle size={24} />}
          color='blue'
          title={t('classroom.gcwSettingsTitle')}
          description={t('classroom.gcwSettingsDescription')}
          to='/teacher/google-classroom'
        />
      )}
    </SimpleGrid>
  )
}
